/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@atlaskit/css';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import { createEndAIExperienceCommand } from '../../../pm-plugins/decoration/actions';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import type { AIDecorationExperiencePluginState } from '../../../pm-plugins/decoration/reducer';
import { hasGeneratedContentDecorations } from '../../../pm-plugins/decoration/utils/hasGeneratedContentDecorations';
import { endInnerEditor } from '../../../pm-plugins/in-editor-streaming/actions';
import type { EndExperience } from '../../../types/types';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { getAIHighlightPositions } from '../../utils/selection';
import { AgentFetcher } from '../AgentFetcher/AgentFetcher';
import { LoadableBrowseRovoAgentsModal } from '../BrowseRovoAgentsModal/BrowseRovoAgentsModal';
import { ExperienceApplication } from '../ExperienceApplication/ExperienceApplication';
import { ModalRegionErrorBoundary } from '../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { GlobalWrapper } from '../GlobalWrapper/GlobalWrapper';
import { ModalRegion } from '../ModalRegion/ModalRegion';
import { PublishToRovo } from '../PublishToRovo/PublishToRovo';
import { RovoEnabled } from '../RovoEnabled/RovoEnabled';
import { SubscribeToRovo } from '../SubscribeToRovo/SubscribeToRovo';

export const ContentComponent = () => {
	const {
		aiOptIn,
		editorPluginAIProvider,
		editorView,
		api,
		wrapperElement,
		appearance,
		providerFactory,
		getMentionNameDetails,
	} = useContentComponentContext();
	const aiDecorationExperiencePluginState = useSharedPluginStateSelector(
		api,
		'aiExperience.aiDecorationExperiencePluginState',
	);
	const rovoAgentsPluginState = useSharedPluginStateSelector(
		api,
		'aiExperience.rovoAgentsPluginState',
	);

	const endExperience: EndExperience = (options) => {
		if (editorPluginAIProvider.onAIProviderChanged) {
			editorPluginAIProvider.onAIProviderChanged('command-palette');
		}

		const state = getPluginState(editorView.state);

		const decoSet =
			hasGeneratedContentDecorations(state) || options?.preserveEditorSelectionOnComplete
				? state.modalDecorationSet
				: undefined;

		const endAIExperienceCommand = createEndAIExperienceCommand(
			decoSet,
			options?.preserveEditorSelectionOnComplete,
		);
		endAIExperienceCommand(editorView.state, editorView.dispatch);

		if (fg('platform_editor_ai_in_editor_streaming')) {
			endInnerEditor(editorView.state, editorView.dispatch);
		}

		editorView.focus();
	};

	// We use this point to render the modal if needed instead of decorators
	// The reason this is preferrable is it has access to mount points
	// where decorators are rendered with a mount point within the Editor content
	// and longer modals get clipped as a result.
	const renderModalRegion = () => {
		// If there are no active decorations -- then we don't want to render the ai experience.
		if (
			!aiDecorationExperiencePluginState ||
			aiDecorationExperiencePluginState.modalDecorationSet === DecorationSet.empty
		) {
			return null;
		}

		if (
			hasGeneratedContentDecorations(
				aiDecorationExperiencePluginState as AIDecorationExperiencePluginState,
			)
		) {
			return null;
		}

		const modalDecorationElement =
			editorView?.dom.querySelector<HTMLElement>('.ai-modal-end-widget') ||
			editorView?.dom.querySelector<HTMLElement>('.ai-selection-node');

		// This protects against cases where there is an active decoration
		// but the document has not been updated to include it
		if (!modalDecorationElement) {
			return null;
		}

		// Prioritise getting position from inlineDecoration,
		// otherwise get decoration position from start or end widget
		let positions: [number, number];
		let decoration =
			aiDecorationExperiencePluginState.modalDecorationSet
				.find(undefined, undefined, (spec) => spec.key === 'inlineDecoration')
				.shift() ||
			aiDecorationExperiencePluginState.modalDecorationSet
				.find(
					undefined,
					undefined,
					(spec) => spec.key === 'startWidgetDecoration' || spec.key === 'endWidgetDecoration',
				)
				.shift();

		if (decoration) {
			// when there is decoration, it means it's text selection
			// we need to use the initial positions
			// as the decoration positions are not accurate when there the selection is empty
			if (!aiDecorationExperiencePluginState?.initialPositions) {
				return null;
			}
			positions = aiDecorationExperiencePluginState?.initialPositions;
		} else {
			const nodeDecorations = aiDecorationExperiencePluginState.modalDecorationSet.find(
				undefined,
				undefined,
				(spec) => spec.key === 'ai-nodeDecoration',
			);
			if (nodeDecorations.length > 0) {
				const startPos = Math.min(...nodeDecorations.map((decoration) => decoration.from));
				const endPos = Math.max(...nodeDecorations.map((decoration) => decoration.to));
				positions = [startPos, endPos];
			}
			decoration = nodeDecorations.shift();
		}

		if (!decoration) {
			return null;
		}

		return (
			<ModalRegion
				lastTriggeredFrom={aiDecorationExperiencePluginState.lastTriggeredFrom}
				key={aiDecorationExperiencePluginState.modalMountedTimeStamp}
				decoration={decoration}
				modalDecorationElement={modalDecorationElement}
				editorView={editorView}
				editorRelativeWrapper={wrapperElement?.offsetParent}
				endExperience={endExperience}
				appearance={appearance}
				decorationSet={aiDecorationExperiencePluginState.modalDecorationSet}
			>
				{aiDecorationExperiencePluginState.configItem && editorView && (
					<ExperienceApplication
						configItem={aiDecorationExperiencePluginState.configItem}
						editorPluginAIProvider={editorPluginAIProvider}
						endExperience={endExperience}
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						positions={positions!}
						editorView={editorView}
						providerFactory={providerFactory}
						lastTriggeredFrom={aiDecorationExperiencePluginState.lastTriggeredFrom}
						triggerMethod={aiDecorationExperiencePluginState.triggerMethod}
						appearance={appearance}
						initialPrompt={aiDecorationExperiencePluginState?.initialPrompt}
						getMentionNameDetails={getMentionNameDetails}
						triggeredFor={aiDecorationExperiencePluginState.triggeredFor}
						engagementPlatformApi={api?.engagementPlatform?.actions}
						initialPositions={aiDecorationExperiencePluginState?.initialPositions}
						nudgeMetrics={aiDecorationExperiencePluginState.nudgeMetrics}
						defaultAgent={rovoAgentsPluginState?.defaultAgent}
					/>
				)}
			</ModalRegion>
		);
	};

	const getPositions = () => {
		return getAIHighlightPositions({
			modalDecorationSet: aiDecorationExperiencePluginState?.modalDecorationSet,
		});
	};

	const renderedSubscribeToRovo = (
		<SubscribeToRovo
			positions={getPositions()}
			editorView={editorView}
			product={editorPluginAIProvider.product}
			onDocChangeByAgent={editorPluginAIProvider.onDocChangeByAgent}
			onAIProviderChanged={editorPluginAIProvider.onAIProviderChanged}
			editorPluginAIProvider={editorPluginAIProvider}
		/>
	);

	return (
		<GlobalWrapper>
			<RovoEnabled editorView={editorView} editorPluginAIProvider={editorPluginAIProvider}>
				<PublishToRovo
					editorView={editorView}
					getMentionNameDetails={getMentionNameDetails}
					editorPluginAIProvider={editorPluginAIProvider}
					editorApi={api}
				/>
				<AgentFetcher editorPluginAIProvider={editorPluginAIProvider} editorView={editorView} />
				{renderedSubscribeToRovo}
			</RovoEnabled>
			<ModalRegionErrorBoundary onCloseFallback={endExperience}>
				{renderModalRegion()}
				{rovoAgentsPluginState?.isBrowseModalOpen && (
					<LoadableBrowseRovoAgentsModal
						editorView={editorView}
						aiGlobalOptIn={aiOptIn}
						editorPluginAIProvider={editorPluginAIProvider}
						startingConfig={aiDecorationExperiencePluginState?.configItem}
						positions={rovoAgentsPluginState?.positions}
					/>
				)}
			</ModalRegionErrorBoundary>
		</GlobalWrapper>
	);
};
